import { useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  action: () => void;
  preventDefault?: boolean;
}

/**
 * Hook to register keyboard shortcuts
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey;
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const altMatch = shortcut.alt ? e.altKey : !e.altKey;
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

        if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
          if (shortcut.preventDefault !== false) {
            e.preventDefault();
          }
          shortcut.action();
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled]);
}

/**
 * Standard POS keyboard shortcuts
 */
export const POS_SHORTCUTS = {
  // Search
  SEARCH: { key: 'f', ctrl: true, description: 'Rechercher produit' },
  BARCODE: { key: 'b', ctrl: true, description: 'Scanner code-barre' },
  
  // Cart Actions
  CHECKOUT: { key: 'F12', description: 'Encaisser' },
  CLEAR_CART: { key: 'F9', description: 'Vider panier' },
  
  // Hold/Retrieve
  HOLD_SALE: { key: 'F2', description: 'Mettre en attente' },
  RETRIEVE_SALE: { key: 'F3', description: 'Récupérer vente' },
  
  // Payment
  CASH_PAYMENT: { key: 'F5', description: 'Paiement espèces' },
  CARD_PAYMENT: { key: 'F6', description: 'Paiement carte' },
  
  // Navigation
  NEXT_CATEGORY: { key: 'Tab', description: 'Catégorie suivante' },
  PREV_CATEGORY: { key: 'Tab', shift: true, description: 'Catégorie précédente' },
  
  // Quantity
  INCREASE_QTY: { key: '+', description: 'Augmenter quantité' },
  DECREASE_QTY: { key: '-', description: 'Diminuer quantité' },
  
  // Customer
  SELECT_CUSTOMER: { key: 'F4', description: 'Sélectionner client' },
  
  // Session
  CLOSE_SESSION: { key: 'F10', description: 'Fermer session' },
  
  // Help
  SHOW_SHORTCUTS: { key: '?', shift: true, description: 'Afficher raccourcis' },
};

/**
 * Format shortcut for display
 */
export function formatShortcut(shortcut: {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
}): string {
  const parts: string[] = [];
  
  if (shortcut.ctrl) parts.push('Ctrl');
  if (shortcut.shift) parts.push('Shift');
  if (shortcut.alt) parts.push('Alt');
  parts.push(shortcut.key);
  
  return parts.join('+');
}
